// Agent session state layer (WCP redesign pass, 2026-09-20).
//
// Screens render affordances from this instead of hardcoding animations. The
// state vocabulary is the one in the WCP design: idle / ready / listening /
// thinking / previewing / acting / waiting_human / done / blocked / speaking.
//
// The value is derived from the live UI-control runtime (real events), so the
// badge and any screen-level affordance reflect what the agent is actually
// doing — not a decorative timer.

import { createContext, useContext, useMemo } from 'react';

import { useUiControlEvents } from './uiControl/useUiControlEvents';
import type { UiRuntimePhase } from './uiControl/runtime';

export type AgentState =
  | 'idle'
  | 'ready'
  | 'listening'
  | 'thinking'
  | 'previewing'
  | 'acting'
  | 'waiting_human'
  | 'done'
  | 'blocked'
  | 'speaking';

export interface AgentSessionValue {
  state: AgentState;
  /** Human label for the badge (never colour alone — accessibility). */
  label: string;
  /** True while the agent is mid-routine. */
  busy: boolean;
  /** Short narration of the current step, when there is one. */
  caption: string | null;
  /** False when the server feature flag is off (no UI control available). */
  enabled: boolean;
}

const LABELS: Record<AgentState, string> = {
  idle: 'Agent idle',
  ready: 'Agent ready',
  listening: 'Listening',
  thinking: 'Thinking',
  previewing: 'Previewing',
  acting: 'Acting on the page',
  waiting_human: 'Waiting for you',
  done: 'Done',
  blocked: 'Blocked',
  speaking: 'Speaking',
};

/** Map the protocol phase onto the design vocabulary. */
export function stateFromPhase(phase: UiRuntimePhase): AgentState {
  switch (phase) {
    case 'connecting':
      return 'thinking';
    case 'connected':
      return 'ready';
    case 'executing':
      return 'acting';
    case 'done':
      return 'done';
    case 'aborted':
    case 'error':
      return 'blocked';
    case 'disabled':
    case 'idle':
    default:
      return 'idle';
  }
}

const AgentSessionContext = createContext<AgentSessionValue>({
  state: 'idle',
  label: LABELS.idle,
  busy: false,
  caption: null,
  enabled: false,
});

export const useAgentSession = () => useContext(AgentSessionContext);

export function AgentSessionProvider({ children }: { children: React.ReactNode }) {
  const runtime = useUiControlEvents();

  const value = useMemo<AgentSessionValue>(() => {
    const state = stateFromPhase(runtime.phase);
    return {
      state,
      label: LABELS[state],
      busy: state === 'acting' || state === 'thinking' || state === 'previewing',
      caption: runtime.caption ?? runtime.spoken ?? null,
      enabled: runtime.enabled,
    };
  }, [runtime.phase, runtime.caption, runtime.spoken, runtime.enabled]);

  return <AgentSessionContext.Provider value={value}>{children}</AgentSessionContext.Provider>;
}

export { LABELS as AGENT_STATE_LABELS };
